<!--src/components/Survey/questions/TeamSelection.svelte-->
<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { scale } from 'svelte/transition';
  
  export let title = '';           // We won't use these directly as they're shown in parent
  export let description = '';     // but they're required props from Question.svelte
  export let options = [];
  export let minSelect = 3;
  export let currentValue = [];
  
  let errorMessage = '';
  let isInitialRender = true;
  
  const dispatch = createEventDispatcher();
  
  onMount(() => {
    // Use a timeout to ensure DOM is fully rendered before enabling animations
    setTimeout(() => {
      isInitialRender = false;
    }, 50);
  });
  
  function toggleTeam(teamId) {
    let newValue;
    
    if (currentValue.includes(teamId)) {
      // Remove team if already selected
      newValue = currentValue.filter(id => id !== teamId);
    } else {
      // Add team if not selected
      newValue = [...currentValue, teamId];
    }
    
    // Clear error message when user makes a selection
    errorMessage = '';
    
    // Dispatch to parent instead of updating local state
    dispatch('select', newValue);
  }
  
  function handleSubmit() {
    if (currentValue.length < minSelect) {
      errorMessage = `Please select at least ${minSelect} teams`;
      return;
    }
    
    // Dispatch advance event to tell Question.svelte to proceed
    dispatch('advance');
  }
</script>

<div class="team-selection">
  {#if errorMessage}
    <div class="error-message">
      {errorMessage}
    </div>
  {/if}
  
  <div class="teams-grid">
    {#each options as team}
      <div 
        class="team-card"
        class:selected={currentValue.includes(team.id)}
        on:click={() => toggleTeam(team.id)}
      >
        <div class="team-logo">
          <img src={team.logo} alt={team.label} />
          
          {#if currentValue.includes(team.id)}
            <div class="selected-indicator" class:no-animation={isInitialRender}>
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path>
              </svg>
            </div>
          {/if}
        </div>
        <div class="team-name">{team.label}</div>
      </div>
    {/each}
  </div>
  
  <div class="selection-footer">
    <div class="selection-count">
      Selected: {currentValue.length} / {minSelect}+
    </div>
    
    <button 
      class="continue-button" 
      disabled={currentValue.length < minSelect}
      on:click={handleSubmit}
    >
      Continue
    </button>
  </div>
</div>

<style>
  .team-selection {
    width: 100%;
  }
  
  .error-message {
    background-color: #ffebee;
    color: #c62828;
    padding: 0.75rem;
    border-radius: 8px;
    margin-bottom: 1rem;
    font-weight: 500;
  }
  
  .teams-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 0.75rem;
    margin-bottom: 2rem;
  }
  
  .team-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.75rem;
    border-radius: 8px;
    border: 2px solid #e0e0e0;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
  }
  
  .team-card:hover {
    border-color: #FFB81C; /* Monsters gold color */
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .team-card.selected {
    border-color: #FFB81C; /* Monsters blue color */
    background-color: rgba(2, 0, 134, 0.05);
  }
  
  .team-logo {
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.5rem;
    position: relative;
  }
  
  .team-logo img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  
  .selected-indicator {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: #FFB81C;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
  }
  
  .selected-indicator:not(.no-animation) {
    animation: pop-in 0.2s ease-out;
  }
  
  @keyframes pop-in {
    0% { transform: scale(0); }
    70% { transform: scale(1.2); }
    100% { transform: scale(1); }
  }
  
  .selected-indicator svg {
    fill: white;
  }
  
  .team-name {
    font-size: 0.9rem;
    color: #333;
    text-align: center;
    font-weight: 500;
  }
  
  .selection-footer {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1rem;
  }
  
  .selection-count {
    font-size: 0.9rem;
    color: #666;
  }
  
  .continue-button {
    background-color: #FFB81C; /* Monsters blue color */
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    border-radius: 30px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    font-weight: 500;
  }
  
  .continue-button:hover:not(:disabled) {
    background-color: #5b89b4;
  }
  
  .continue-button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    .teams-grid {
      grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    }
    
    .team-logo {
      width: 50px;
      height: 50px;
    }
  }
</style>